import { type ChartArea, type Chart, type FontSpec } from "chart.js";
import { set } from "lodash";
import { type ChartJSOrUndefined } from "react-chartjs-2/dist/types";

import {
  drawRect,
  withContextRestore,
} from "@features/mmm/utils/context-helpers";
import { drawLine } from "@features/sales-v3/utils/canvas";

import {
  OBSERVED_SPEND_COLOR,
  OBSERVED_SPEND_ID,
  REVENUE_COLOR,
  REVENUE_ID,
  ROAS_BORDER_DASH,
  ROAS_COLOR,
  ROAS_ID,
} from "../const";

export const Y_SCALE_TITLES_PLUGIN_ID = "yScaleTitles";

const Y_AXIS_TITLE_ORDER = [ROAS_ID, REVENUE_ID, OBSERVED_SPEND_ID];

const drawAlternativeMetricTitle = (
  ctx: CanvasRenderingContext2D,
  metricName = "",
  {
    x,
    font,
    tickWidth,
    chartArea: { top, bottom },
    fillStyle,
    lineDash = [],
    barOptions,
  }: {
    font: FontSpec;
    x: number;
    tickWidth: number;
    chartArea: ChartArea;
    fillStyle: string;
    lineDash?: number[];
    barOptions?: {
      color: string;
    };
  },
) => {
  withContextRestore(ctx, () => {
    ctx.font = `${font.weight} ${font.size}px ${font.family}`;

    ctx.strokeStyle = fillStyle;
    ctx.lineWidth = 2;

    ctx.setLineDash(lineDash);

    drawLine(ctx, { x, y: top - 1 }, { x, y: bottom + 1 });
    drawLine(ctx, { x, y: top }, { x: x + tickWidth, y: top });
    drawLine(ctx, { x, y: bottom }, { x: x + tickWidth, y: bottom });

    const measuredText = ctx.measureText(metricName);
    const textWidth = measuredText.width;
    const textHeight =
      measuredText.actualBoundingBoxAscent +
      measuredText.actualBoundingBoxDescent;

    const XOffset = x + textHeight / 2;
    const YOffset = (top + bottom) / 2 + textWidth / 2;

    ctx.fillStyle = "white";
    ctx.fillRect(
      x - textHeight / 2,
      (top + bottom) / 2 - textWidth / 2 - 5,
      textHeight,
      textWidth + 10,
    );

    ctx.fillStyle = fillStyle;
    ctx.translate(XOffset, YOffset);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText(metricName, 0, 0);

    if (barOptions) {
      ctx.rotate(Math.PI / 2);
      drawRect(ctx, -10, 4, 12, 22, "white");
      drawRect(ctx, -10, 4, 12, 16, barOptions.color);
    }
  });
};

export type YScaleTitlesPluginPluginOptions = {
  titles?: Record<string, string>;
  font?: FontSpec;
};

export const yScaleTitlesPlugin = {
  id: Y_SCALE_TITLES_PLUGIN_ID,
  options: {
    titles: {},
    font: {},
  } as YScaleTitlesPluginPluginOptions,
  afterDraw(
    chart: Chart,
    _args: any,
    options: YScaleTitlesPluginPluginOptions,
  ) {
    const { ctx, chartArea } = chart;
    const kpiScale = chart.scales[REVENUE_ID];
    const roasScale = chart.scales[ROAS_ID];
    const isEfficiencyMetricVisible = isAnyDatasetVisible(chart, ROAS_ID);
    const isKpiScaleVisible = isAnyDatasetVisible(chart, REVENUE_ID);
    const observedSpendScale = chart.scales[OBSERVED_SPEND_ID];
    const isObservedSpendScaleVisible = isAnyDatasetVisible(
      chart,
      OBSERVED_SPEND_ID,
    );

    const kpiMetric = options.titles?.[REVENUE_ID] ?? "";
    const roasMetric = options.titles?.[ROAS_ID] ?? "";
    const observedSpendMetric = options.titles?.[OBSERVED_SPEND_ID] ?? "";
    const font = {
      family: "Inter",
      size: 12,
      weight: "600",
      style: "normal",
      lineHeight: 16,
    } as FontSpec;

    if (isEfficiencyMetricVisible) {
      drawAlternativeMetricTitle(ctx, roasMetric, {
        font,
        x: 24,
        tickWidth: 12,
        chartArea,
        fillStyle: ROAS_COLOR,
        lineDash: ROAS_BORDER_DASH,
      });
    }

    if (isKpiScaleVisible) {
      drawAlternativeMetricTitle(ctx, kpiMetric, {
        font,
        x: kpiScale.right - 10,
        tickWidth: -12,
        chartArea,
        fillStyle: REVENUE_COLOR,
      });
    }

    if (isObservedSpendScaleVisible) {
      drawAlternativeMetricTitle(ctx, observedSpendMetric, {
        font,
        x: observedSpendScale.right - 2,
        tickWidth: -12,
        chartArea,
        fillStyle: "#7081FF",
        barOptions: {
          color: OBSERVED_SPEND_COLOR,
        },
      });
    }
  },
};

type SetYAxesTitlesParams = {
  chart: ChartJSOrUndefined | null;
  naming: {
    [metric: string]: string;
  };
};

const isAnyDatasetVisible = (chart: Chart, yAxisID: string) => {
  const { datasets } = chart.config.data;
  const isVisible = datasets.some(
    // @ts-ignore
    (dataset) => dataset.yAxisID === yAxisID && !dataset.hidden,
  );
  if (chart.config.options?.scales?.[yAxisID]?.display !== isVisible) {
    set(chart, `config.options.scales.${yAxisID}.display`, isVisible);
    chart.update();
  }
  return isVisible;
};

export const setYAxesTitles = ({ chart, naming }: SetYAxesTitlesParams) => {
  if (chart) {
    Y_AXIS_TITLE_ORDER.forEach((axisId) => {
      set(
        chart,
        `config.options.plugins.${Y_SCALE_TITLES_PLUGIN_ID}.titles.${axisId}`,
        naming[axisId].toUpperCase(),
      );
    });
    setYAxesTitlesPadding(chart);
    chart.update();
  }
};

// Makes the chart title padding updates
const setYAxesTitlesPadding = (chart: Chart) => {
  set(chart, `config.options.scales.${REVENUE_ID}.title.display`, true);
  set(chart, `config.options.scales.${REVENUE_ID}.title.padding`, 0);
  set(chart, `config.options.scales.${ROAS_ID}.title.display`, true);
  set(chart, `config.options.scales.${ROAS_ID}.title.padding`, 0);
  set(chart, `config.options.scales.${OBSERVED_SPEND_ID}.title.display`, true);
  set(chart, `config.options.scales.${OBSERVED_SPEND_ID}.title.padding`, 0);
};
